import Email from '@crehana/icons/Email';
import Update from '@crehana/icons/fill/Update';
import AlertCircle from '@crehana/icons/outline/AlertCircle';
import CheckmarkCircle2 from '@crehana/icons/outline/CheckmarkCircle2';
import Download from '@crehana/icons/outline/Download';
import FirstPage from '@crehana/icons/outline/FirstPage';
import Pause from '@crehana/icons/Pause';
import Trash from '@crehana/icons/Trash';
import { CustomNextImage as Image } from '@crehana/next';
import {
  colors,
  Dialog,
  DialogContent,
  InverseButton,
  PrimaryButton,
  // biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
} from '@crehana/ui';
import { useMe, useUITheme } from '@crehana/web';
import { clsx } from 'clsx';

import { imgixLoader } from '@/shared/utils/imgixLoader';

import { type InfoDialogProps, InfoDialogTypeEnum } from './types';

const InfoDialog: React.FC<InfoDialogProps> = ({
  open,
  type,
  title,
  description,
  descriptionRight,
  icon,
  headerImage,
  onDismiss,
  onConfirm,
  buttonLabel,
}) => {
  const { me } = useMe();
  const { isDark } = useUITheme();
  const { baseLighter } = colors;

  const RenderImage = () => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (!headerImage) return <></>;
    return (
      <Image
        loader={imgixLoader}
        alt="InfoDialog_image"
        height={112}
        src={headerImage || ''}
        width={112}
        className="mb-16"
      />
    );
  };

  const RenderIcon = () => {
    const iconProps = {
      color: baseLighter,
      size: 48,
      className: 'mb-12',
    };
    if (icon) {
      const { component: Icon, ...customIconProps } = icon;
      return <Icon {...iconProps} {...customIconProps} />;
    }
    switch (type) {
      case InfoDialogTypeEnum.Email:
        return <Email {...iconProps} />;
      case InfoDialogTypeEnum.Download:
        return <Download {...iconProps} />;
      case InfoDialogTypeEnum.Error:
        return <AlertCircle {...iconProps} />;
      case InfoDialogTypeEnum.Success:
        return <CheckmarkCircle2 {...iconProps} />;
      case InfoDialogTypeEnum.Delete:
        return <Trash {...iconProps} />;
      case InfoDialogTypeEnum.Back:
        return <FirstPage {...iconProps} />;
      case InfoDialogTypeEnum.Pause:
        return <Pause {...iconProps} />;
      case InfoDialogTypeEnum.Resume:
        return <Update {...iconProps} />;
      case InfoDialogTypeEnum.Info:
      default:
        return <></>;
    }
  };

  const DialogActions = () => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (!onConfirm)
      return (
        <div className="w-full flex px-16 lg:px-24 justify-evenly">
          <PrimaryButton
            label={buttonLabel}
            onClick={() => onDismiss(type)}
            className={clsx(
              'm-auto w-full sm:w-auto',
              isDark && 'bg-primary-light',
            )}
          />
        </div>
      );
    return (
      <div className="w-full flex flex-col-reverse sm:flex-row px-16 lg:px-24 justify-evenly">
        <InverseButton
          label="Cancelar"
          onClick={() => onDismiss(type)}
          isDark={isDark}
          className="m-auto my-8 w-full sm:w-auto"
          isGray
        />
        <PrimaryButton
          label={buttonLabel}
          onClick={() => onConfirm(type)}
          isDark={isDark}
          className={clsx(
            'm-auto my-8 w-full sm:w-auto',
            type === InfoDialogTypeEnum.Delete ||
              type === InfoDialogTypeEnum.Back
              ? 'bg-red-main hover:bg-red-light'
              : isDark && 'bg-primary-light',
          )}
        />
      </div>
    );
  };

  return (
    <Dialog
      isDark={isDark}
      open={open}
      onDismiss={() => onDismiss(type)}
      size="s"
      data-testid="info-dialog"
    >
      <DialogContent className="flex flex-col justify-center items-center">
        {headerImage ? <RenderImage /> : <RenderIcon />}
        {Boolean(title) && (
          <p
            className={clsx(
              'font-h4 mb-16 text-center pl-72 pr-72 ',
              isDark ? 'text-white' : 'text-base-main',
            )}
          >
            {title}
          </p>
        )}
        {Boolean(description) && (
          <p
            className={clsx(
              'whitespace-pre-wrap justify-center font-body3 text-center mb-24 ',
              isDark ? 'text-white' : 'text-base-main',
            )}
          >
            {description}
            <span
              className={clsx(
                'ml-4 mr-4 font-body3',
                isDark ? 'text-secondary-light' : 'text-primary-light',
              )}
            >
              {type === InfoDialogTypeEnum.Email && me?.email && (
                <strong>{` ${me?.email} `}</strong>
              )}
            </span>
            {descriptionRight}
          </p>
        )}
        <DialogActions />
      </DialogContent>
    </Dialog>
  );
};

export default InfoDialog;
